import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Autocomplete,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useFormik } from "formik";
import * as Yup from "yup";
import AddNewLawyerDialog from "./AddNewLawyerDialog";
import AddNewClientDialog from "./AddNewClientDialog";
import { CMSContext } from "../components/CaseManagement/context/CaseManagmentContext";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { showInsufficientCreditsModal } from "../actions/creditActions";
import { deductCredits } from "../utils/HelperFunction/deductCredits";
import moment from "moment";
import { createCaseApi, updateCaseApi } from "../services/api/caseManagementApi";

const AddNewCaseDialog = ({
  open,
  onClose,
  data,
  addNewClient = false,
  addNewLawyer = false,
}) => {
  const { advocates, clients, fetchCases } = useContext(CMSContext);
  const [loading, setLoading] = useState(false);
  const [includeSelf, setIncludeSelf] = useState(false);
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const ownerId = userInfo._id;
  const [nestedOpen, setNestedOpen] = useState(null);
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      caseTitle: data?.caseTitle || "",
      caseDescription: data?.caseDescription || "",
      advocates: Array.isArray(data?.advocates)
        ? data.advocates
            .map((adv) => {
              const advocate = advocates.find(
                (advocate) => advocate._id === adv.id
              );
              return advocate ? { lawyer: advocate, role: adv.role } : null;
            })
            .filter(Boolean)
        : [{ lawyer: null, role: "" }],
      clients: Array.isArray(data?.clients)
        ? data.clients
            .map((id) => clients.find((client) => client._id === id))
            .filter(Boolean)
        : [],
      startDate: data?.startDate
        ? moment(data?.startDate).format("YYYY-MM-DD")
        : "",
      endDate:
        data?.endDate && data?.endDate !== "Ongoing"
          ? moment(data?.endDate).format("YYYY-MM-DD")
          : "",
      status: data?.status || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      caseTitle: Yup.string().required("Case title is required"),
      caseDescription: Yup.string().required("Case description is required"),
      advocates: addNewClient
        ? Yup.array()
        : Yup.array()
            .of(
              Yup.object().shape({
                lawyer: Yup.object().nullable().required("Lawyer is required"),
                role: Yup.string().required("Role is required"),
              })
            )
            .min(1, "At least one advocate is required"),
      clients: addNewLawyer
        ? Yup.array()
        : Yup.array().min(1, "At least one client is required"),
      startDate: Yup.date().required("Start date is required"),
      endDate: Yup.date().min(
        Yup.ref("startDate"),
        "End date cannot be before start date"
      ),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        if (data?._id) {
          await saveCaseApi(values, "PUT");
        } else {
          await saveCaseApi(values, "POST");
        }
        resetForm();
        onClose();
      } catch (error) {
        setLoading(false);
        console.error("Error saving case:", error);
      }
    },
  });

  const handleCheckboxChange = (event) => {
    setIncludeSelf(event.target.checked);
    if (event.target.checked) {
      const filteredAdvocates = formik.values.advocates.filter(
        (advocate) => advocate.lawyer !== null && advocate.role !== ""
      );

      const updatedAdvocates = [
        {
          lawyer: {
            _id: "",
            name: userInfo?.name,
            email: userInfo?.email,
            userId: userInfo?._id,
          },
          role: "Owner",
        },
        ...filteredAdvocates,
      ];

      formik.setFieldValue("advocates", [...updatedAdvocates]);
    } else {
      const updatedAdvocates = formik.values.advocates.filter(
        (advocate) => advocate.lawyer?.userId !== userInfo?._id
      );
      formik.setFieldValue(
        "advocates",
        updatedAdvocates.length > 0
          ? updatedAdvocates
          : [{ lawyer: null, role: "" }]
      );
    }
  };

  const saveCaseApi = async (caseData, method) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }

    const updatedCaseData = {
      ...caseData,
      createdBy: ownerId,
      caseOwner: userInfo?.name || "",
      advocates:
        caseData?.advocates?.map((advocate) => ({
          id: advocate.lawyer._id,
          name: advocate.lawyer?.name,
          email: advocate.lawyer?.email,
          userId: advocate.lawyer.userId,
          role: advocate.role,
        })) || [],
      clients: caseData?.clients?.map((v) => v._id),
      includeSelf: includeSelf,
    };

    const response =
      method === "PUT"
        ? await updateCaseApi(data._id, updatedCaseData)
        : await createCaseApi(updatedCaseData);

    if (!(response.status === 200 || response.status === 201)) {
      const errorText =
        method === "PUT" ? "Failed to update case" : "Failed to save case";
      toast.error(errorText);
      setLoading(false);
      throw new Error(errorText);
    }
    const responseData = response.data;
    const successText =
      method === "PUT"
        ? "Case Updated Successfully"
        : responseData?.message || "Case created successfully";
    toast.success(successText);
    if (method === "PUT") {
      fetchCases(data._id);
    } else {
      fetchCases(true);
    }

    setLoading(false);
    setIncludeSelf(false);
    onClose();
    const deductionSuccess = deductCredits(
      {
        userId,
        creditsToDeduct: 1,
        event: `${
          method === "PUT"
            ? "Edit Client on Case Management"
            : "Add new Client on Case Management"
        }`,
      },
      dispatch
    );

    if (!deductionSuccess) {
      console.warn("Failed to deduct credits. Ensure retry or user notification.");
    }
    return responseData;
  };

  const handleAddNested = (type) => {
    setNestedOpen(type);
  };

  const handleCloseNested = () => {
    setNestedOpen(null);
  };

  const handleModalClose = () => {
    formik.resetForm();
    setIncludeSelf(false);
    onClose();
  };

  const handleAddLawyer = () => {
    formik.setFieldValue("advocates", [
      ...formik.values.advocates,
      { lawyer: null, role: "" },
    ]);
  };

  const handleRemoveLawyer = (index) => {
    const updated = formik.values.advocates.filter((_, i) => i !== index);
    formik.setFieldValue("advocates", updated);
  };

  const roleOptions = ["Owner", "Contributor", "Viewer"];

  useEffect(() => {
    if (data?.advocates?.find((adv) => adv.userId === ownerId)) {
      setIncludeSelf(true);
    }
  }, [data, ownerId]);

  return (
    <>
      <Dialog
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 4,
            boxShadow: 24,
            p: 1,
            width: 500,
          },
        }}
        open={open}
        onClose={handleModalClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: "18px",
            fontWeight: "bold",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          {addNewClient
            ? "Add Client to Case"
            : addNewLawyer
              ? "Add Lawyer to Case"
              : data
                ? "Edit Case"
                : "Add New Case"}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <TextField
                label="Case Title"
                fullWidth
                required
                name="caseTitle"
                size="small"
                variant="outlined"
                value={formik.values.caseTitle}
                onChange={formik.handleChange}
                error={formik.touched.caseTitle && Boolean(formik.errors.caseTitle)}
                helperText={formik.touched.caseTitle && formik.errors.caseTitle}
                disabled={addNewClient || addNewLawyer}
                InputProps={{
                  sx: {
                    bgcolor: "white",
                    fontSize: "14px",
                    "& input:-webkit-autofill": {
                      backgroundColor: "white !important",
                      WebkitBoxShadow: "0 0 0 30px white inset !important",
                      WebkitTextFillColor: "black !important",
                    },
                  },
                }}
                InputLabelProps={{ sx: { fontSize: "14px" } }}
              />
              <TextField
                label="Case Description"
                required
                multiline
                rows={3}
                size="small"
                fullWidth
                name="caseDescription"
                value={formik.values.caseDescription}
                onChange={formik.handleChange}
                error={
                  formik.touched.caseDescription &&
                  Boolean(formik.errors.caseDescription)
                }
                helperText={
                  formik.touched.caseDescription && formik.errors.caseDescription
                }
                disabled={addNewClient || addNewLawyer}
                InputProps={{
                  sx: {
                    bgcolor: "white",
                    fontSize: "14px",
                    "& input:-webkit-autofill": {
                      backgroundColor: "white !important",
                      WebkitBoxShadow: "0 0 0 30px white inset !important",
                      WebkitTextFillColor: "black !important",
                    },
                  },
                }}
                InputLabelProps={{ sx: { fontSize: "14px" } }}
              />
              <Grid container spacing={2}>
                {formik.values.advocates?.map((entry, index) => (
                  <Grid item xs={12} key={index}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={5.5}>
                        <Autocomplete
                          fullWidth
                          size="small"
                          disabled={addNewClient || (includeSelf && index === 0)}
                          value={formik.values.advocates[index].lawyer || null}
                          options={[
                            ...(advocates?.filter(
                              (advocate) =>
                                !formik.values.advocates.some(
                                  (item, i) =>
                                    i !== index &&
                                    item.lawyer?._id === advocate._id
                                )
                            ) || []),
                            { name: `+ Add New Advocate`, isAddNew: true },
                          ]}
                          getOptionLabel={(option) => option?.name || ""}
                          filterOptions={(options = [], state) => {
                            const filtered = options?.filter((option) =>
                              option.name
                                .toLowerCase()
                                .includes(state.inputValue.toLowerCase())
                            );

                            if (!filtered.length && state.inputValue) {
                              return [
                                { name: `+ Add New Advocate`, isAddNew: true },
                                ...filtered,
                              ];
                            }

                            return filtered;
                          }}
                          onChange={(event, newValue, reason, details) => {
                            if (details?.option?.isAddNew) {
                              handleAddNested("advocate");
                              formik.setFieldValue(`advocates[${index}].lawyer`, []);
                            } else {
                              formik.setFieldValue(
                                `advocates[${index}].lawyer`,
                                newValue || null
                              );
                            }
                          }}
                          componentsProps={{
                            paper: {
                              sx: {
                                "& .MuiAutocomplete-listbox": {
                                  fontSize: "13px",
                                  maxHeight: "120px",
                                  overflowY: "auto",
                                  "::-webkit-scrollbar": { width: "4px" },
                                  "::-webkit-scrollbar-track": { background: "#fff" },
                                  "::-webkit-scrollbar-thumb": { background: "#bbb", borderRadius: "4px" },
                                },
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              size="small"
                              label="Select Lawyer"
                              error={
                                formik.touched.advocates?.[index]?.lawyer &&
                                Boolean(formik.errors.advocates?.[index]?.lawyer)
                              }
                              helperText={
                                formik.touched.advocates?.[index]?.lawyer &&
                                formik.errors.advocates?.[index]?.lawyer
                                  ? formik.errors.advocates?.[index]?.lawyer
                                  : formik.errors.advocates?.[index]?.role &&
                                      formik.touched.advocates?.[index]?.role
                                    ? " "
                                    : ""
                              }
                              InputProps={{ ...params.InputProps, sx: { fontSize: "14px" } }}
                              InputLabelProps={{ sx: { fontSize: "14px" } }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={5.5}>
                        <Autocomplete
                          fullWidth
                          disabled={addNewClient || (includeSelf && index === 0)}
                          size="small"
                          options={roleOptions}
                          value={formik.values.advocates[index].role || null}
                          onChange={(event, newValue) => {
                            formik.setFieldValue(
                              `advocates[${index}].role`,
                              newValue || ""
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              label="Role"
                              error={
                                formik.touched.advocates?.[index]?.role &&
                                Boolean(formik.errors.advocates?.[index]?.role)
                              }
                              helperText={
                                formik.touched.advocates?.[index]?.role &&
                                formik.errors.advocates?.[index]?.role
                                  ? formik.errors.advocates?.[index]?.role
                                  : formik.errors.advocates?.[index]?.lawyer &&
                                      formik.touched.advocates?.[index]?.lawyer
                                    ? " "
                                    : ""
                              }
                              InputLabelProps={{ sx: { fontSize: "14px" } }}
                              inputProps={{ ...params.inputProps, style: { fontSize: "14px" } }}
                            />
                          )}
                          ListboxProps={{
                            sx: {
                              fontSize: "13px",
                              maxHeight: "120px",
                              overflowY: "auto",
                              "&::-webkit-scrollbar": { width: "4px" },
                              "&::-webkit-scrollbar-track": { background: "#fff" },
                              "&::-webkit-scrollbar-thumb": { background: "#bbb", borderRadius: "4px" },
                            },
                          }}
                        />
                      </Grid>
                      {index > 0 && (
                        <Grid item xs={1}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveLawyer(index)}
                          >
                            <RemoveCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                ))}
                {(!addNewClient || addNewLawyer) && (
                  <Grid item xs={12}>
                    <Box display="flex">
                      <Button
                        size="small"
                        sx={{ textTransform: "none" }}
                        startIcon={<AddIcon />}
                        onClick={handleAddLawyer}
                      >
                        Add Lawyer
                      </Button>
                    </Box>
                    <FormControlLabel
                      sx={{
                        "& .MuiFormControlLabel-label": { fontSize: "13px" },
                        "& .MuiSvgIcon-root": { fontSize: 18 },
                      }}
                      control={
                        <Checkbox
                          size="small"
                          checked={includeSelf}
                          onChange={handleCheckboxChange}
                          color="primary"
                        />
                      }
                      label="Add me as a lawyer"
                    />
                  </Grid>
                )}
              </Grid>
              <Autocomplete
                disabled={addNewLawyer}
                required
                multiple
                options={clients || []}
                noOptionsText={
                  <span style={{ fontSize: "14px" }}>No clients added yet</span>
                }
                value={formik.values.clients || []}
                size="small"
                getOptionLabel={(option) => option?.name || ""}
                filterOptions={(options = [], state) => {
                  const filtered = options?.filter((option) =>
                    option.name
                      .toLowerCase()
                      .includes(state.inputValue.toLowerCase())
                  );

                  if (state.inputValue && !filtered.length) {
                    return [
                      ...filtered,
                      { name: `+ Add New Client`, isAddNew: true },
                    ];
                  }

                  return filtered;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Select Clients *"
                    error={
                      formik.touched.clients && Boolean(formik.errors.clients)
                    }
                    helperText={formik.touched.clients && formik.errors.clients}
                    InputLabelProps={{ sx: { fontSize: "14px" } }}
                  />
                )}
                componentsProps={{
                  paper: {
                    sx: {
                      "& .MuiAutocomplete-listbox": {
                        fontSize: "13px",
                        maxHeight: "120px",
                        overflowY: "auto",
                        "::-webkit-scrollbar": { width: "4px" },
                        "::-webkit-scrollbar-track": { background: "#fff" },
                        "::-webkit-scrollbar-thumb": { background: "#bbb", borderRadius: "4px" },
                      },
                    },
                  },
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) =>
                    option.isAddNew ? null : (
                      <Chip
                        sx={{
                          padding: 0,
                          fontSize: "12px",
                          m: 0,
                          height: "unset",
                          "& .MuiSvgIcon-root ": { fontSize: "18px" },
                          "& .MuiChip-label": { paddingX: "8px" },
                        }}
                        key={option.name}
                        label={option.name}
                        {...getTagProps({ index })}
                      />
                    )
                  )
                }
                onChange={(event, newValue, reason, details) => {
                  if (details?.option?.isAddNew) {
                    handleAddNested("client");
                    const validAdvocates = newValue?.filter((v) => !v.isAddNew) || [];
                    formik.setFieldValue("clients", validAdvocates);
                  } else {
                    formik.setFieldValue("clients", newValue || []);
                  }
                }}
                isOptionEqualToValue={(option, value) => option._id === value._id}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    disabled={addNewClient || addNewLawyer}
                    required
                    label="Start Date"
                    type="date"
                    fullWidth
                    name="startDate"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.startDate}
                    onChange={formik.handleChange}
                    error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                    helperText={formik.touched.startDate && formik.errors.startDate}
                    InputProps={{
                      sx: {
                        bgcolor: "white",
                        fontSize: "14px",
                        "& input:-webkit-autofill": {
                          backgroundColor: "white !important",
                          WebkitBoxShadow: "0 0 0 30px white inset !important",
                          WebkitTextFillColor: "black !important",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled={addNewClient || addNewLawyer}
                    label="End Date"
                    type="date"
                    fullWidth
                    name="endDate"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.endDate}
                    onChange={formik.handleChange}
                    error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                    helperText={formik.touched.endDate && formik.errors.endDate}
                    InputProps={{
                      sx: {
                        bgcolor: "white",
                        fontSize: "14px",
                        "& input:-webkit-autofill": {
                          backgroundColor: "white !important",
                          WebkitBoxShadow: "0 0 0 30px white inset !important",
                          WebkitTextFillColor: "black !important",
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Autocomplete
                disabled={addNewClient || addNewLawyer}
                size="small"
                options={[
                  { title: "In Progress", color: "orange" },
                  { title: "Not Started", color: "red" },
                  { title: "Completed", color: "green" },
                ]}
                getOptionLabel={(option) => option.title}
                value={
                  [
                    { title: "In Progress", color: "orange" },
                    { title: "Not Started", color: "red" },
                    { title: "Completed", color: "green" },
                  ].find((option) => option.title === formik.values.status) || null
                }
                onChange={(e, newValue) => {
                  formik.setFieldValue("status", newValue ? newValue.title : "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    sx={{ fontSize: "14px", "& .MuiInputBase-input": { fontSize: "14px" } }}
                    label="Status"
                    error={formik.touched.status && Boolean(formik.errors.status)}
                    helperText={formik.touched.status && formik.errors.status}
                    InputLabelProps={{ sx: { fontSize: "14px" } }}
                  />
                )}
                componentsProps={{
                  paper: {
                    sx: {
                      "& .MuiAutocomplete-listbox": {
                        fontSize: "13px",
                        maxHeight: "120px",
                        overflowY: "auto",
                        "::-webkit-scrollbar": { width: "4px" },
                        "::-webkit-scrollbar-track": { background: "#fff" },
                        "::-webkit-scrollbar-thumb": { background: "#bbb", borderRadius: "4px" },
                      },
                    },
                  },
                }}
                isOptionEqualToValue={(option, value) => option.title === value?.title}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                onClose();
                setIncludeSelf(false);
                formik.resetForm();
              }}
              sx={{
                borderRadius: "24px",
                padding: "8px 24px",
                textTransform: "none",
                fontWeight: "bold",
              }}
              variant="outlined"
              color="error"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: "24px",
                padding: "8px 24px",
                textTransform: "none",
                fontWeight: "bold",
                backgroundColor: "#003D78",
                "&:hover": { backgroundColor: "primary.main", color: "#fff" },
                transition: "background-color 0.3s ease-in-out",
              }}
              disabled={loading}
            >
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <CircularProgress size={18} sx={{ color: "#a9a9a9" }} />
                  <Box>Adding...</Box>
                </Box>
              ) : (
                "Add Case"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {nestedOpen === "advocate" && (
        <AddNewLawyerDialog open={true} onClose={handleCloseNested} />
      )}
      {nestedOpen === "client" && (
        <AddNewClientDialog open={true} onClose={handleCloseNested} />
      )}
    </>
  );
};

export default AddNewCaseDialog;
